import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

import Button from '../common/Button';
import { useAuthStore } from '../../store/authStore';
import { useReadingsStore } from '../../store/readingsStore';

const inputClasses = "w-full px-4 py-2.5 border border-mist dark:border-dusk rounded-lg focus:outline-none focus:ring-2 focus:ring-sun/40 dark:focus:ring-ember/40 focus:border-sun dark:focus:border-ember bg-parchment/60 dark:bg-shadow/60 text-ink dark:text-parchment font-sans placeholder:text-mist transition-colors duration-200";

const SignUpPage: React.FC = () => {
  const navigate = useNavigate();
  const { signUp, loading, error } = useAuthStore();
  const { restorePendingReading } = useReadingsStore();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');

    if (!email || !password || !confirmPassword || !displayName) {
      setValidationError('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      setValidationError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setValidationError('Password must be at least 8 characters long');
      return;
    }

    await signUp(email, password, displayName);
    if (!error) {
      if (restorePendingReading()) {
        navigate('/interpretation');
      } else {
        navigate('/');
      }
    }
  };

  return (
    <div className="min-h-[calc(100vh-8rem)] bg-parchment dark:bg-ink">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md mx-auto py-16 px-4"
      >
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="font-display text-4xl md:text-5xl font-semibold text-ink dark:text-parchment">
            Create Your Account
          </h1>
          <p className="mt-3 text-ink/60 dark:text-parchment/50 font-sans text-sm">
            Join Tara's Tarot to save your readings and track your journey.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {(error || validationError) && (
            <div className="bg-sun/10 dark:bg-ember/10 text-sun dark:text-ember p-3 rounded-lg flex items-center gap-2 text-sm">
              <span>{error || validationError}</span>
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-display text-ink/80 dark:text-parchment/60 mb-1.5">
              Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClasses}
              placeholder="Enter your email"
            />
          </div>

          <div>
            <label htmlFor="displayName" className="block text-sm font-display text-ink/80 dark:text-parchment/60 mb-1.5">
              Display Name
            </label>
            <input
              type="text"
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className={inputClasses}
              placeholder="Choose a display name"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-display text-ink/80 dark:text-parchment/60 mb-1.5">
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClasses}
              placeholder="Create a password"
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-display text-ink/80 dark:text-parchment/60 mb-1.5">
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={inputClasses}
              placeholder="Confirm your password"
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            fullWidth
            isLoading={loading}
          >
            Sign Up
          </Button>

          <p className="text-center text-ink/60 dark:text-parchment/50 font-sans text-sm">
            Already have an account?{' '}
            <Link to="/login" className="font-display text-sun dark:text-ember hover:text-ember dark:hover:text-sun transition-colors">
              Log In
            </Link>
          </p>
        </form>
      </motion.div>
    </div>
  );
};

export default SignUpPage;
